<template>
  <div>
    <W2HButton v-if="isParticipantInbox && showTimelineButton" icon="history" text="Timeline" @click="timelineButtonPressed = !timelineButtonPressed" />
    <div class="row inbox-component parent">
      <div v-if="isParticipantInbox" v-show="showTimeline" class="col-3 pr-0" @mouseleave="hideConversationTimeline">
        <ConversationTimeline
          ref="ConversationTimeline"
          :class="{'mt-1': showTimelineButton}"
          :inboxHelper="inboxHelper"
          :store="store"
          :selectedConversationId="selectedConversationId"
          :force-show="timelineButtonPressed"
          @reselect="selectConversationId"
        />
      </div>
      <div :class="['h-100', isInboxFullWidth ? 'col-12': 'col-9' ]">
        <label class="input-toggle float-right">
          <input v-model="galleryView" type="checkbox">
          <span class="toggle-slider" /> Media View
        </label>
        <p>
          Time Zone: {{ inboxHelper.getFriendlyTimezoneName() }}
        </p>
        <div>
          <div class="position-relative">
            <div ref="inbox" class="inbox clearfix" @scroll="handleMessageScroll">
              <button
                v-if="!galleryView && !unreadMessages"
                ref="jumpToPresentButton"
                :class="['jump-to-present btn btn-default fas fa-arrow-down', {visible: isAboveBottom}]"
                aria-label="Jump to present"
                @click="goToBottom"
              />
              <button
                v-if="!galleryView && unreadMessages > 0"
                ref="jumpToPresentButton"
                class="jump-to-present jump-full btn btn-default visible"
                aria-label="Jump to present"
                @click="goToBottom"
              >
                <span class="fas fa-arrow-down" />
                <span class="font-weight-semibold small ml-2">{{ unreadMessages }} new message(s)</span>
              </button>
              <div class="position-relative">
                <GalleryView
                  v-if="galleryView"
                  :messages-with-images="messagesWithImages"
                  :store="store"
                  :inbox-helper="inboxHelper"
                  :show-load-more="store.loading.older"
                  @openImageLightbox="openImageLightbox"
                />
                <MessageView
                  v-else
                  ref="messageView"
                  :sorted-messages="sortedMessages"
                  :store="store"
                  :inbox-helper="inboxHelper"
                  :show-load-more="store.loading.older"
                  :show-ai-icon="!readOnly && aiSuggestionsEnabled"
                  :embed-context="embedContext"
                  @openImageLightbox="openImageLightbox"
                  @suggestResponse="suggestResponse"
                />
              </div>
            </div>
            <ImageLightbox
              ref="imageLightbox"
              v-model="showImageLightbox"
              :store="store"
            />
          </div>
        </div>
        <hr>
        <MessagingModeSwitcher
          v-if="isParticipantInbox"
          :manual-mode-helper="manualModeHelper"
          :store="store"
          :reply-window-configuration="replyWindowConfiguration"
          :has-pending-texts="hasPendingTexts"
        />

        <AiResponseRequestRejectModal
          :store="store"
          @submit="rejectSuggestedResponse"
        />
        <div v-if="!readOnly">
          <InputArea
            v-model="textContent"
            :canned-responses="store.cannedResponses"
            :disabled="disableTextInput"
            :upload-url="uploadUrl"
            :image-name.sync="imageName"
            :image-path.sync="imageUrl"
          />
          <div v-if="!store.aiGeneratedResponse" class="inbox-action-items">
            <div v-if="imageUrl">
              <h4>Attachments</h4>
              <ul class="list-unstyled">
                <li>
                  <span class="filename">{{ imageName }}</span>
                  <button
                    class="btn btn-link"
                    type="button"
                    @click="removeAttachment"
                  >
                    <span class="fa fa-trash text-danger" />
                    <span class="sr-only">Remove</span>
                  </button>
                </li>
              </ul>
            </div>
            <input
              v-if="!store.loading.send"
              type="submit"
              value="Send"
              :class="styleConfig.inboxSubmit"
              :disabled="store.loading.send"
              @click="sendMessage"
            >

            <div
              v-else
              :class="styleConfig.inboxSubmit"
              class="disabled"
            >
              Send
              <b-spinner
                small
                variant="light"
                label="Spinning"
              />
            </div>

            <button
              v-if="aiSuggestionsEnabled && store.selectedMessage && !store.loading.suggestResponse"
              type="submit"
              :class="[styleConfig.inboxSuggestResponse, 'suggest-response-button', {'selected': store.selectedMessage, 'loading': store.loading.suggestResponse}]"
              :disabled="store.loading.suggestResponse && !store.selectedMessage"
              @click="suggestResponse"
            >
              Suggest Response
              <b-icon icon="stars" class="ml-1" />
            </button>
            <div
              v-else-if="aiSuggestionsEnabled && !store.selectedMessage || store.loading.suggestResponse"
              id="ai-response-info"
              :class="[styleConfig.inboxSuggestResponse, 'suggest-response-button', {'selected': store.selectedMessage, 'loading': store.loading.suggestResponse}]"
            >
              Suggest Response
              <b-spinner
                v-if="store.loading.suggestResponse"
                small
                variant="light"
                label="Spinning"
              />
              <span v-if="!store.selectedMessage">
                <b-icon
                  id="ai-response-info-icon"
                  icon="stars"
                  variant="light"
                  font-scale="1"
                  class="ml-1"
                />
              </span>
              <b-popover
                target="ai-response-info"
                title="AI generated responses are enabled"
                triggers="click blur"
                placement="right"
              >
                Select an inbound message above and click "Suggest Response" to generate a response.
              </b-popover>
            </div>
          </div>
          <div v-else class="inbox-action-items mt-1">
            <b-button
              variant="primary"
              :disabled="store.loading.send"
              @click="sendSuggestedResponse"
            >
              <b-icon icon="hand-thumbs-up" />
              Send
            </b-button>
            <b-button
              variant="danger"
              :disabled="store.loading.send"
              @click="openRejectCommentModal"
            >
              <b-icon icon="hand-thumbs-down" />
              Reject
            </b-button>
            <b-button
              variant="secondary"
              :disabled="store.loading.send"
              :class="{'refresh-button': true, 'selected': store.selectedMessage, 'loading': store.loading.refreshResponse}"
              @click="refreshSuggestedResponse"
            >
              <b-icon icon="arrow-clockwise" />
              Refresh
              <b-spinner
                v-if="store.loading.refreshResponse"
                small
                variant="light"
                label="Spinning"
              />
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import InboxStore from "./stores/inbox";
import styles from './stores/styles';
import inboxHelper from './helpers/inbox';
import manualModeHelper from './helpers/manualMode';
import ImageLightbox from "./components/ImageLightbox.vue";
import InputArea from "@/FormWidgets/WidgetInputArea/InputArea.vue";
import GalleryView from "./components/GalleryView.vue";
import MessageView from "./components/MessageView.vue";
import AiResponseRequestRejectModal from "./components/AiResponseRequestRejectModal.vue";
import ConversationTimeline from "@/Inbox/components/ConversationTimeline.vue";
import MessagingModeSwitcher from "@/Inbox/components/MessagingModeSwitcher.vue";
import {throttle} from "lodash-es";
import W2HButton from "@/FormWidgets/W2HButton/W2HButton.vue";

export default {
  name: "InboxComponent",
  components: {
    W2HButton,
    MessagingModeSwitcher,
    ConversationTimeline,
    GalleryView,
    InputArea,
    ImageLightbox,
    MessageView,
    AiResponseRequestRejectModal
  },
  props: {
    auth: {
      type: Object,
      required: false,
      default() {
        return {
          method: "session",
          apiKey: ""
        }
      }
    },
    pollFrequencySeconds: {
      type: Number,
      required: false,
      default: 10,
    },
    apiBaseUrl: {
      type: String,
      required: true
    },
    studyId: {
      type: Number,
      required: true
    },
    participantId: {
      type: Number,
      required: true
    },
    resource: {
      type: String,
      default: "participants",
      validator: function (value) {
        // The value must match one of these
        return ['participants', 'support_partners'].includes(value);
      },
    },
    replyWindowConfiguration: {
      type: [Object, Array],
      default() {
        return {};
      }
    },
    aiSuggestionsEnabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    uploadUrl: {
      type: String,
      required: true,
    },
    styles: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    embedContext: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      selectedConversationId: '0',
      styleConfig: Object.assign(styles, this.styles),
      store: new InboxStore(
          this.apiBaseUrl,
          this.participantId,
          this.resource,
          this.studyId,
          this.auth
      ),
      inboxHelper,
      manualModeHelper,
      textContent: "",
      imageUrl: "",
      imageName: "",
      showImageLightbox: false,
      galleryView: false,
      throttledHandler: null,
      throttledLoadAfterScrollHandler: null,
      atPageBottom: true,
      scrollLoadThreshold: 0.2, // Trigger loading when 20% from top
      initialLoad: true,
      isPolling: false,
      pollTimer: null,
      isLoadingOlderMessages: false,
      isLoadingNewerMessages: false,
      isSmoothScrolling: false,
      viewMode: 'live', // 'live' or 'historical'
      showTimelineButton: false,
      timelineButtonPressed: false, // used when window is small
    }
  },
  computed: {
    isParticipantInbox() {
      return this.resource === 'participants';
    },
    isReducedMotion() {
      return window.matchMedia(`(prefers-reduced-motion: reduce)`) === true
      || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;
    },
    isAboveBottom() {
      return !this.atPageBottom;
    },
    disableTextInput() {
      return Boolean(this.store.loading.send)
          || Boolean(this.store.loading.suggestResponse)
          || Boolean(this.store.loading.refreshResponse);
    },
    hasMessages() {
      return Object.keys(this.store.messagesObj).length > 0;
    },
    showTimeline() {
      return !this.galleryView && (!this.showTimelineButton || this.timelineButtonPressed);
    },
    isInboxFullWidth() {
      return this.galleryView || this.showTimelineButton || !this.isParticipantInbox;
    },
    sortedMessages() {
      if (this.hasMessages) {
        return this.inboxHelper.sortMessages(Object.values(this.store.messagesObj));
      }
      return [];
    },
    messagesWithImages() {
      return Object.values(this.store.messagesObj).filter(msg => msg.media.length > 0).reverse();
    },
    latestMessageId() {
      // Note that if this.store.messagesObj is set to {}, this will return -Infinity
      return Math.max(...Object.keys(this.store.messagesObj));
    },
    hasPendingTexts() {
      if (!this.manualModeHelper.isManualModeActive) {
        return false;
      }
      const since = this.manualModeHelper.session?.start_time.split(' ')[0];
      return Boolean(this.sortedMessages.find((msg) => msg.created_at >= since && msg.status === 'queued'));
    },
    unreadMessages() {
      return this.store.unreadMessages;
    }
  },
  watch: {
    latestMessageId() {
      this.$nextTick(() => {
        if (this.initialLoad) {
          this.initialLoad = false;
          this.scrollToNewest('instant');
          return;
        }
        if (this.galleryView || this.viewMode === 'live' || (this.atPageBottom && !this.hasNewerMessages)) {
          this.scrollToNewest('smooth');
        }
      });
    },
    galleryView() {
      // scroll to newest when changing views
      if (this.galleryView) {
        this.checkAndReloadNewest();
      }
    },
  },
  created() {
    this.throttledHandler = throttle(() => this.store.resetManualModeTimeoutCounter(), 1000);
    document.addEventListener('mousemove', this.throttledHandler);
    document.addEventListener('mouseup', this.throttledHandler);
    document.addEventListener('keyup', this.throttledHandler);
    document.addEventListener('touchend', this.throttledHandler);
    document.addEventListener('touchmove', this.throttledHandler);
    window.addEventListener('resize', this.onResize);

    this.throttledLoadAfterScrollHandler = throttle((inboxElement, direction) => {
        this.loadMessagesAfterScroll(inboxElement, direction);
    }, 200);
  },
  beforeDestroy() {
    if (this.throttledHandler) {
      this.throttledHandler.cancel();
    }
    if (this.throttledLoadAfterScrollHandler) {
      this.throttledLoadAfterScrollHandler.cancel();
    }

    this.stopPolling();
    document.removeEventListener('mousemove', this.throttledHandler);
    document.removeEventListener('mouseup', this.throttledHandler);
    document.removeEventListener('keyup', this.throttledHandler);
    document.removeEventListener('touchend', this.throttledHandler);
    document.removeEventListener('touchmove', this.throttledHandler);
    window.removeEventListener('resize', this.onResize);
  },
  async mounted() {
    // Make sure we detect window size on load
    this.onResize();
    await this.store.loadMessages();
    await this.store.getCannedResponses();
    this.scrollToNewest();
    this.startPolling();
    this.$refs.inbox.addEventListener('scrollend', () => {
      this.isSmoothScrolling = false;
    });
  },
  methods: {
    onResize() {
      // todo: have this flag also be true if there's a touchscreen
      this.showTimelineButton = window.innerWidth < 475;
      this.timelineButtonPressed = false;
    },
    scrollToMessageWithId(id) {
      const messageList = this.$refs.messageView;
      if (!messageList || undefined === messageList.$refs[`message-${id}`][0]) {
        return;
      }
      this.isSmoothScrolling = true;
      messageList.$refs[`message-${id}`][0].scrollIntoView({
        behavior: this.isReducedMotion ? 'auto' : 'smooth',
        block: 'center',
        container: 'nearest'
      });
      this.focusMessageWithId(id);
    },
    // For keyboard navigation
    focusMessageWithId(id) {
      const messageList = this.$refs.messageView;
      if (!messageList || undefined === messageList.$refs[`message-${id}`][0]) {
        return;
      }
      messageList.$refs[`message-${id}`][0].focus({preventScroll: true});
    },
    async checkAndReloadNewest(behavior = 'auto') {
      if(this.hasNewerMessages || (this.unreadMessages > 0 && this.viewMode === 'historical')){
        await this.store.fetchAroundTimestamp(new Date().toISOString());
        await this.$nextTick();
      }
      this.scrollToNewest(behavior);
      this.returnToLiveMode();
    },
    removeAttachment() {
      this.imageUrl = '';
      this.imageName = '';
    },
    openImageLightbox(msgId, imageIndex) {
      this.showImageLightbox = true;
      this.$refs.imageLightbox.open(msgId, imageIndex, this.galleryView);
    },
    openRejectCommentModal() {
      this.$bvModal.show('reject-comment-modal');
    },
    async goToBottom() {
      this.selectedConversationId = "0";
      await this.checkAndReloadNewest('smooth');
      if (this.sortedMessages.length > 0) {
        this.focusMessageWithId(this.sortedMessages.slice(-1)[0].id); // focus last message
      }
    },
    handleMessageScroll(e) {
      const inboxElement = e.target;
      if (!inboxElement) {
        return;
      }

      if (this.galleryView) {
        this.handleGalleryScroll(inboxElement);
        return;
      }

      const {scrollTop, offsetHeight, scrollHeight} = inboxElement;
      this.atPageBottom = (scrollTop + offsetHeight) + 300 >= scrollHeight;

      if (scrollHeight > 0 && !this.isSmoothScrolling) {
        const scrollPercentage = scrollTop / scrollHeight;

        // Check if we're at top 20% of the inbox and not already doing a merge/scroll
        if (scrollPercentage <= this.scrollLoadThreshold && !this.store.loading.older && !this.isLoadingOlderMessages) {
          this.isLoadingOlderMessages = true;
          if (this.throttledLoadAfterScrollHandler) {
            this.throttledLoadAfterScrollHandler(inboxElement, 'older');
          }
          this.isLoadingOlderMessages = false;
        }

        // Check if we're at bottom 20% of the inbox and not already doing a merge/scroll
        if ((1-scrollPercentage) <= this.scrollLoadThreshold && !this.store.loading.newer && !this.isLoadingNewerMessages) {
          this.isLoadingNewerMessages = true;
          if (this.throttledLoadAfterScrollHandler) {
            this.throttledLoadAfterScrollHandler(inboxElement, 'newer');
          }
          this.isLoadingNewerMessages = false;
          if (this.store.loadedNewestMessage) {
            this.returnToLiveMode();
          }
        }
      }
    },
    async handleGalleryScroll(inboxElement) {
      if (this.store.loading.older || !inboxElement) {
        return;
      }

      const {scrollTop, offsetHeight, scrollHeight} = inboxElement;
      if (scrollHeight > 0 && scrollTop >= scrollHeight - offsetHeight - 1) {
        await this.store.loadOlderLegacy();
      }
    },
    async loadMessagesAfterScroll(inboxElement, direction = 'older') {
      const hasPending = direction === 'newer' ?
        await this.store.loadNewer() :
        await this.store.loadOlder();
      if (!hasPending || !inboxElement) {
        return;
      }

      const scrollTopBefore = inboxElement.scrollTop;
      const scrollHeightBefore = inboxElement.scrollHeight;

      this.store.mergePendingMessages();

      const scrollHeightAfter = inboxElement.scrollHeight;
      const heightDifference = scrollHeightAfter - scrollHeightBefore;

      const targetScrollTop = scrollTopBefore + heightDifference;
      inboxElement.scrollTop = Math.max(0, targetScrollTop);
    },
    scrollToNewest(behavior = 'auto') {
      const inbox = this.$refs.inbox;
      if (!inbox) {
        return;
      }
      // Go to the most recent messages, at bottom of MessageView or the top of GalleryView
      const scrollToOffset = this.galleryView ? 0 : inbox.scrollHeight;

      if (behavior === 'smooth') {
        this.isSmoothScrolling = true;
      }

      if (typeof inbox.scrollTo === 'function') {
        inbox.scrollTo({top: scrollToOffset, behavior: this.isReducedMotion ? 'auto' : behavior});
      } else {
        // IE fallback
        inbox.scrollTop = scrollToOffset;
      }
    },
    returnToLiveMode() {
      this.viewMode = 'live';
      this.store.unreadMessages = 0;
      this.hasNewerMessages = false;
    },
    async sendMessage() {
      if (this.textContent.length || this.imageUrl) {
        const success = await this.store.sendMessage(this.textContent, this.imageUrl);

        if (!success) {
          return;
        }

        if (this.isParticipantInbox && this.manualModeHelper.isAutomatedModeActive) {
          await this.store.enableManualMode();
        }
        this.resetContent('smooth');
        await this.checkAndReloadNewest();
      }
    },
    async sendSuggestedResponse() {
      if (this.textContent.length && this.store.aiGeneratedResponse) {
        await this.store.sendSuggestedMessage(this.textContent, this.imageUrl);
        if (this.isParticipantInbox && this.manualModeHelper.isAutomatedModeActive) {
          await this.store.enableManualMode();
        }
        this.resetContent();
        await this.checkAndReloadNewest('smooth');
      }
    },
    async rejectSuggestedResponse(comment) {
      if (this.store.aiGeneratedResponse) {
        await this.store.rejectSuggestedMessage(comment);
        this.$bvModal.hide('reject-comment-modal');
        this.resetContent();
      }
    },
    resetContent() {
      this.$refs.messageView?.clearSelection();
      this.textContent = "";
      this.removeAttachment();
    },
    async refreshSuggestedResponse() {
      await this.store.refreshSuggestedMessage();
      this.textContent = this.store.aiGeneratedResponse.response;
    },
    async suggestResponse() {
      await this.store.suggestResponse();
      this.textContent = this.store.aiGeneratedResponse.response;
    },
    startPolling() {
      if (this.pollTimer) {
        return;
      }

      this.pollTimer = setInterval(async () => {
        if (this.isPolling) {
          return;
        }
        this.isPolling = true;
        try {
          if (this.viewMode === 'live' || this.atPageBottom) {
            await this.store.poll(true);
          } else {
            await this.store.poll(false);
          }
        } catch (e) {
          console.error('Error while message polling: ', e);
        } finally {
          this.isPolling = false;
        }
      }, this.pollFrequencySeconds * 1000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    getBoundingMessages() {
      return {oldest: this.sortedMessages[0], newest: this.sortedMessages.slice(-1)[0]};
    },
    messagesContainTimestamp(timestamp) {
      const messages = this.getBoundingMessages();
      return (messages.oldest.sent_at || messages.oldest.created_at) <= timestamp &&
        (messages.newest.sent_at || messages.newest.created_at) >= timestamp;
    },
    async selectConversationId(conversation) {
      if(this.sortedMessages.length === 0) {
        return;
      }

      // When this is our oldest convo, it may be older than our oldest text, so check that we've loaded the oldest messages
      // Otherwise, just check that the convo timestamp exists within our current message range
      const earliestConversation = this.store.conversations[0].id === conversation.id;
      const requiresMessageLoad = earliestConversation ?
        !this.store.loadedOldestMessage :
        !this.messagesContainTimestamp(conversation.created_at);

      // Only apply logic when there are enough messages to potentially need to reload
      if (this.sortedMessages.length > 3 && requiresMessageLoad) {
        this.viewMode = 'historical';

        const previousBoundingMessages = this.getBoundingMessages();
        await this.store.fetchAroundTimestamp(conversation.created_at);

        // If our previous set of messages had a more recent message than our current
        if(!this.messagesContainTimestamp(previousBoundingMessages.newest.sent_at || previousBoundingMessages.newest.created_at)){
          this.hasNewerMessages = true;
        }
        // If our previous set of messages had an older message than our current
        if(!this.messagesContainTimestamp(previousBoundingMessages.oldest.sent_at || previousBoundingMessages.oldest.created_at)){
          this.store.loadedOldestMessage = false;
        }
      }

      const bestMatch = this.sortedMessages.find(message =>
        message.study_user_conversation_id.toString() === conversation.id.toString()
      );
      // force MessageView to update selected conversation before setting selected message
      if(this.selectedConversationId === conversation.id) {
        this.selectedConversationId = '0';
      }
      this.selectedConversationId = conversation.id.toString();
      if (bestMatch && bestMatch.id) {
        this.scrollToMessageWithId(bestMatch.id);
      }
    },
    hideConversationTimeline() {
      // mouseleave can sometimes trigger while navigating the timeline filtering modal, so ignore the event if it's visible
      const timeline = this.$refs.ConversationTimeline;
      if (timeline && timeline.$refs.timelineFilterModal.show) {
        return;
      }
      this.timelineButtonPressed = false;
    }
  },
}
</script>

<style scoped>
.parent {
  overflow: hidden;
  container-type: inline-size;
}

.inbox {
  max-height: 60vh;
  min-height: 60vh;
  overflow-x: hidden;
  overflow-y: scroll;
  border-bottom: 2px solid rgba(0, 0, 0, .2);
  /* Give space for jump button, so it doesn't show up over message text */
  padding-right: 10px;
}

.link-container {
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
}

@media print {
  /* Note: this reveals all the currently loaded messages */
  .parent, .inbox {
    max-height: none;
    height: auto;
  }
}

::v-deep .time {
  float: right;
  margin: 0 3px 0;
  color: gray;
}

::v-deep .status-icon {
  float: left;
  cursor: pointer;
  margin: 0 3px 0;
}

::v-deep .cursor-pointer {
  cursor: pointer;
}

::v-deep .suggest-response-button {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

::v-deep .suggest-response-button.selected {
  background: linear-gradient(135deg,
    #6366f1 0%,
    #8b5cf6 25%,
    #d1d5db 50%,
    #6366f1 75%,
    #8b5cf6 100%
  );
  background-size: 400% 400%;
  color: white;
}

::v-deep .suggest-response-button.selected.loading {
  animation: gradientShift 3s ease-in-out infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

::v-deep .refresh-button {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

::v-deep .refresh-button.selected {
  background: linear-gradient(135deg,
    #6366f1 0%,
    #8b5cf6 25%,
    #d1d5db 50%,
    #6366f1 75%,
    #8b5cf6 100%
  );
  background-size: 400% 400%;
  color: white;
}

::v-deep .refresh-button.selected.loading {
  animation: gradientShift 3s ease-in-out infinite;
}

.jump-to-present {

  &.jump-full {
    width: fit-content;
    height: 36px;
    border-radius: 30px;
  }

  text-align: center;
  position: absolute;
  bottom: 30px;
  right: 15px;
  /* Put above MessageView items: */
  z-index: 1;
  translate: 100px 0;

  width: 36px;
  height: 36px;
  font-size: 14px;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: .4s translate;
  @media (prefers-reduced-motion: reduce) {
    transition: 0.01s translate
  }

  &:focus {
    transition: none;
    translate: 0 0;
    bottom: 30px;
    right: 10px;
  }

  /* Used in computed.isAboveBottom */

  &.visible {
    translate: 0 0;
  }
}
</style>
